<script setup lang="ts">

import { computed, watch } from 'vue'
import { useTableStore } from '@/stores/tableStore.ts'
import ActionBarBlock from '@/components/Interface/DataTable/ActionBar/ActionBarBlock.vue'
import { logger } from '@/models/logger';

import ActionBarFiller from '@/components/Interface/DataTable/ActionBar/ActionBarFiller.vue'
import Pagination from '@/components/Interface/DataTable/Pagination.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const tableStore = useTableStore()

// Define table state type for dynamic access
type TableState = {
  page: number
  rowsPerPage: number
  totalRows: number
  totalFilteredRows: number
  visibleRows: number
  selectedRowIds: number[]
  selectedAll: boolean
  sortColumn: string
  sortDirection: string
  singleSelectedId: number
  filters: Record<string, unknown>
}

const props = defineProps({
  selector: {
    type: Boolean,
    default: false
  },
  footerBar: {
    type: Boolean,
    default: true
  },
  noResultMessage: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: 'id'
  },
  tableStoreName: {
    type: String,
    required: true
  },
  filters: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: Function as unknown as () => (list: any[]) => any[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    default: (list: any[]) => list
  },
  heightLimit: {
    type: String,
    default: 'h-[calc(100vh-10rem)]'
  },
  data: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: Map as unknown as () => Map<number, any>,
    required: true
  },
  click: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: Function as unknown as () => (id: any) => void,
    default: () => {}
  },
  doubleClick: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    type: Function as unknown as () => (id: any) => void,
    default: () => {}
  }
});

// Helper to access table state with proper typing
const getTableState = (): TableState => {
  return (tableStore as unknown as Record<string, TableState>)[props.tableStoreName]
}

// Computed for template usage
const tableState = computed(() => getTableState())

//Sorting
const list = computed(() => {
  const state = getTableState()
  const sortedList = Array.from(props.data.values()).sort((a, b) => {
    const sortColumn = state.sortColumn;
    const sortDirection = state.sortDirection;

    // Access the values to compare
    const valueA = a[sortColumn];
    const valueB = b[sortColumn];

    // Handle numbers
    if (typeof valueA === 'number' && typeof valueB === 'number') {
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    }

    // Handle strings (or other types)
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      if (sortDirection === 'asc') {
        return valueA.localeCompare(valueB);
      } else {
        return valueB.localeCompare(valueA);
      }
    }

    // handle boolean
    if (typeof valueA === 'boolean' && typeof valueB === 'boolean') {
      return sortDirection === 'asc' ? (valueA ? 1 : 0) - (valueB ? 1 : 0) : (valueB ? 1 : 0) - (valueA ? 1 : 0);
    }

    // Fallback case for other types or undefined values
    return 0;
  });

  const filteredList = props.filters(sortedList)
  const start = Number(state.page * state.rowsPerPage);
  const end = start + Number(state.rowsPerPage);
  const paginatedList = filteredList.slice(start, end);

  return paginatedList
});

// Computed properties for store updates (without side effects)
const filteredList = computed(() => props.filters(Array.from(props.data.values())));
const visibleRowCount = computed(() => list.value.length);

// Watch for changes and update store accordingly
watch(filteredList, (newFilteredList) => {
  getTableState().totalFilteredRows = newFilteredList.length;
}, { immediate: true });

watch(visibleRowCount, (newCount) => {
  getTableState().visibleRows = newCount;
}, { immediate: true });

watch(() => getTableState().rowsPerPage, () => {
    const state = getTableState()
    state.page = 0
    state.selectedRowIds = []
    state.selectedAll = false
})

watch(() => getTableState().page, () => {
  getTableState().selectedAll = false
})

watch(
  () => getTableState().filters,
  () => {
    const state = getTableState()
    state.page = 0;
    state.selectedRowIds = []
    state.selectedAll = false
  },
  { deep: true }
);


//Select all button
const selectAllHandler = () => {
  const state = getTableState()
  if (!state.selectedAll) {
    // Select all visible rows
    for (const row of list.value) {
      selectRow(row.id)
    }
    state.selectedAll = true
  } else {
    // Deselect all visible rows
    for (const row of list.value) {
      deselectRow(row.id);
    }
  }
}

//Select one row
const selectRowHandler = (id:number) => {
  const state = getTableState()
  const selectedIndex = state.selectedRowIds.indexOf(id);
  if (selectedIndex === -1) {
    // Add the id if it's not already selected
    selectRow(id)
  } else {
    // Remove the id if it's already selected
    deselectRow(id)
  }
  logger.debug(state.selectedRowIds);
}

const clickHandler = (id:number) => {
  getTableState().singleSelectedId = id
  props.click(id)
}

const doubleClickHandler = (id:number) => {
  props.doubleClick(id)
}

//Select row sub method
const selectRow = (id:number) => {
  const state = getTableState()
  const selectedIndex = state.selectedRowIds.indexOf(id);
  if (selectedIndex === -1) {
    logger.debug('selectRow',id)
    state.selectedRowIds.push(id);
  }
}

//Deselect row sub method
const deselectRow = (id:number) => {
  const state = getTableState()
  const selectedIndex = state.selectedRowIds.indexOf(id);
  if (selectedIndex !== -1) {
    logger.debug('deselectRow',id)
    state.selectedRowIds.splice(selectedIndex, 1);
  }
  state.selectedAll = false
}

// Select Sorting
const sortColumn = (column:string) => {
  const state = getTableState()
  if(state.sortColumn === column) {
    if(state.sortDirection === 'asc') {
      state.sortDirection = 'desc'
    } else {
      state.sortDirection = 'asc'
    }
  } else {
    state.sortColumn = column
    state.sortDirection = 'asc'
  }
}

</script>

<template>
  <div :class="'grid grid-rows-[auto_auto_1fr_auto] overflow-x-auto no-scrollbar ' + props.heightLimit">
    <!--- ActionBar--->
    <div :class="{
      'flex justify-end pb-1' : $slots.actionBar
    }">
      <slot name="actionBar">
      </slot>
    </div>
    <div v-if="$slots.actionBar2" class="flex justify-end pb-1">
      <slot name="actionBar2">
      </slot>
    </div>
    <!--- Headers --->
    <div class="flex h-10 bg-slate-700 rounded-t">
      <div v-if="props.selector" class="flex items-center justify-center w-12">
        <slot name="selectorHeader" :selectAllHandler="selectAllHandler" :selectedAll="tableState.selectedAll">
          <input
            type="checkbox"
            :checked="tableState.selectedAll"
            @click.stop="selectAllHandler"
            class="w-6 h-6 accent-slate-600 cursor-pointer rounded"
          >
        </slot>
      </div>
      <slot name="columns" :sort="sortColumn" :sortColumn="tableState.sortColumn" :sortDirection="tableState.sortDirection">
      </slot>
    </div>

    <!--- Rows --->
    <div class="overflow-y-scroll no-scrollbar" >
      <template v-for="item in list" :key="item.id">
        <div
          :class="{
              'flex border-b border-b-slate-700 h-12': true,
              'bg-slate-800': tableState.singleSelectedId==item.id,
              'cursor-pointer': true
            }"
          @dblclick.stop="doubleClickHandler(item.id)"
          @click.stop="clickHandler(item.id)"
        >
          <div v-if="props.selector" class="flex items-center justify-center w-12">
            <input
              type="checkbox"
              :checked="tableState.selectedRowIds.includes(item.id)"
              @click.stop="selectRowHandler(item.id)"
              class="w-6 h-6 accent-slate-600 bg-slate-600 cursor-pointer rounded"
            >
          </div>
          <slot name="rows" :item="item" >
          </slot>
        </div>
      </template>
      <div v-if="tableState.totalFilteredRows == 0" class="p-6 w-full flex justify-center text-subtext">
        {{ props.noResultMessage || t('common.status.noResults') }}
      </div>
    </div>

    <!--- Footer --->
    <div v-if="footerBar" class="flex justify-end h-10">
      <ActionBarBlock class="mr-1 px-2 flex items-center text-subtext rounded">
        {{ t('common.pagination.total', { count: tableState.totalFilteredRows }) }}
      </ActionBarBlock>
      <ActionBarBlock v-if="props.selector" class="mr-1 px-2 flex items-center text-subtext rounded">
        {{ t('common.pagination.selected', { count: tableState.selectedRowIds.length }) }}
      </ActionBarBlock>
      <ActionBarFiller class="mr-1 rounded"></ActionBarFiller>
      <ActionBarBlock class="flex mr-1 items-center rounded">
        <Pagination :tableStoreName="tableStoreName"></Pagination>
      </ActionBarBlock>
      <ActionBarBlock class="flex px-2 items-center rounded">
        <select v-model="tableState.rowsPerPage" class="!bg-slate-700 rounded">
          <option>10</option>
          <option>25</option>
          <option>50</option>
          <option>100</option>
          <option>1000</option>
        </select>
      </ActionBarBlock>
    </div>
  </div>
</template>

<style scoped lang="scss">
select {
  @apply bg-slate-600 p-1 text-slate-400 focus:outline-none
}
</style>